import type { CSSProperties, ReactNode } from 'react';
import { flushSync } from 'react-dom';
import { createRoot } from 'react-dom/client';
import type { LogisticsQueueOrder } from '../api/orders';

// Landscape, inverted header, no barcode: an InPost label is A6 portrait with a barcode, and a box
// carrying this one must never be mistaken for a parcel InPost will collect.
const PAGE_CSS =
  '@page{size:A6 landscape;margin:0}' +
  'html,body{margin:0;padding:0;background:#fff}' +
  '*{-webkit-print-color-adjust:exact;print-color-adjust:exact}';

const SANS = "'Arial Narrow', 'Helvetica Neue', Arial, sans-serif";
const MONO = "ui-monospace, 'SFMono-Regular', Menlo, Consolas, monospace";

export function OwnLabel({ order, demo }: { order: LogisticsQueueOrder; demo: boolean }) {
  const abroad = order.shippingMethod === 'zagranica';
  const intl = order.shippingAddress.international;
  const country = (intl?.countryName ?? intl?.country ?? '—').toUpperCase();
  const totalShirts = order.items.reduce((s, i) => s + i.qty, 0);

  return (
    <div
      data-testid="own-label"
      style={{
        width: '148mm',
        height: '105mm',
        boxSizing: 'border-box',
        border: '1.5mm solid #000',
        background: '#fff',
        color: '#000',
        fontFamily: SANS,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        WebkitPrintColorAdjust: 'exact',
        printColorAdjust: 'exact',
      }}
    >
      <div
        style={{
          background: '#000',
          color: '#fff',
          padding: '2mm 4mm',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '3mm',
        }}
      >
        <span style={{ fontSize: '8mm', fontWeight: 900, letterSpacing: '0.3mm', lineHeight: 1 }}>
          {abroad ? `ZAGRANICA · ${country}` : 'ODBIÓR OSOBISTY'}
        </span>
        <span style={{ fontSize: '2.8mm', fontWeight: 700, letterSpacing: '0.4mm', whiteSpace: 'nowrap' }}>
          OPERACJA TENERYFA
        </span>
      </div>
      <div
        style={{
          borderBottom: '0.6mm solid #000',
          padding: '1.2mm 4mm',
          fontSize: '2.8mm',
          fontWeight: 800,
          letterSpacing: '0.3mm',
        }}
      >
        {abroad
          ? 'ETYKIETA WEWNĘTRZNA SKLEPU · TO NIE JEST ETYKIETA INPOST'
          : 'NIE NADAWAĆ · WYDANIE NA KONCERCIE · TO NIE JEST ETYKIETA INPOST'}
      </div>

      <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', minHeight: 0 }}>
        <div style={{ padding: '3mm 4mm', borderRight: '0.6mm solid #000', minWidth: 0 }}>
          <Caption>ZAMÓWIENIE</Caption>
          <div style={{ fontFamily: MONO, fontSize: '6mm', fontWeight: 900, lineHeight: 1.1 }}>
            {order.id}
          </div>
          <Caption top>ODBIORCA</Caption>
          <div style={{ fontSize: '4.6mm', fontWeight: 800, lineHeight: 1.15, overflowWrap: 'anywhere' }}>
            {order.customerName.toUpperCase()}
          </div>
          <div style={{ fontFamily: MONO, fontSize: '3mm', marginTop: '0.8mm' }}>TEL {order.phone}</div>
          <div style={{ fontFamily: MONO, fontSize: '3mm' }}>BILET {order.externalOrderId ?? '—'}</div>
          {abroad && (
            <>
              <Caption top>NR PRZESYŁKI PRZEWOŹNIKA</Caption>
              <div
                style={{
                  fontFamily: MONO,
                  fontSize: '4.2mm',
                  fontWeight: 900,
                  overflowWrap: 'anywhere',
                }}
              >
                {order.trackingNumber ?? '—'}
              </div>
            </>
          )}
        </div>

        <div style={{ padding: '3mm 4mm', minWidth: 0 }}>
          <Caption>ZAWARTOŚĆ · {totalShirts} SZT</Caption>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '3.2mm', marginTop: '1mm' }}>
            <tbody>
              {order.items.map((item, i, arr) => (
                <tr
                  key={i}
                  style={{ borderBottom: i < arr.length - 1 ? '0.3mm dashed #000' : 'none' }}
                >
                  <td style={{ padding: '0.8mm 0', fontWeight: 800 }}>
                    {item.color.toUpperCase()} · {item.cut.toUpperCase()}
                    {item.fromOrderId && (
                      <div style={{ fontFamily: MONO, fontSize: '2.4mm', fontWeight: 400 }}>
                        Z {item.fromOrderId}
                      </div>
                    )}
                  </td>
                  <td style={{ textAlign: 'center', fontWeight: 900, fontSize: '3.8mm' }}>{item.size}</td>
                  <td style={{ textAlign: 'right', fontFamily: MONO }}>×{item.qty}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {demo && (
        <div
          style={{
            borderTop: '0.6mm dashed #000',
            padding: '1mm 4mm',
            fontSize: '2.6mm',
            fontWeight: 800,
            textAlign: 'center',
            letterSpacing: '0.3mm',
          }}
        >
          DEMO · PODGLĄD · NIC NIE ZOSTAŁO ZMIENIONE
        </div>
      )}
    </div>
  );
}

function Caption({ children, top }: { children: ReactNode; top?: boolean }) {
  const style: CSSProperties = {
    fontSize: '2.5mm',
    fontWeight: 700,
    letterSpacing: '0.4mm',
    marginTop: top ? '2.4mm' : 0,
    marginBottom: '0.6mm',
  };
  return <div style={style}>{children}</div>;
}

/**
 * Renders the label into a window the caller opened inside the click gesture, then opens the
 * print dialog there. The kiosk's own stylesheet never reaches that window, which is why the
 * label carries every style inline.
 */
export function printOwnLabel(win: Window, order: LogisticsQueueOrder, demo: boolean) {
  const doc = win.document;
  doc.open();
  doc.write(
    `<!doctype html><html lang="pl"><head><meta charset="utf-8"><style>${PAGE_CSS}</style></head>` +
      '<body><div id="own-label-root"></div></body></html>',
  );
  doc.close();
  doc.title = `Etykieta ${order.id}`;
  const root = createRoot(doc.getElementById('own-label-root')!);
  flushSync(() => root.render(<OwnLabel order={order} demo={demo} />));
  win.focus();
  win.print();
}
